// src/components/workflows/nodes/HttpRequestNode.jsx - HTTP Request Node (Display Only)
import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { Badge } from '../../ui/badge.tsx';
import { Zap, CheckCircle2 } from 'lucide-react';

/**
 * HttpRequestNode - Displays automation execution steps (display only)
 *
 * Node data structure:
 * {
 *   type: 'httpRequest',
 *   data: {
 *     label: 'Execute Scraper',
 *     description: 'Runs the invoice scraper automation',
 *     steps: [
 *       'Log into InvoiceCloud portal',
 *       'Navigate to open invoices',
 *       'Extract invoice data',
 *       'Send results to Daybloom'
 *     ],
 *     // Technical config (hidden from display)
 *     method: 'POST',
 *     url: 'https://...',
 *     headers: {...},
 *     body: {...}
 *   }
 * }
 */
const HttpRequestNode = ({ data, selected, id }) => {
  const {
    label = 'Execute Automation',
    description,
    steps = []
  } = data;

  return (
    <>
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        style={{ left: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-purple-500"
      />

      <div className={`
        w-64 rounded-lg border bg-slate-900
        ${selected ? 'ring-2 ring-purple-500 shadow-lg border-purple-500/50' : 'shadow-md border-purple-500/20'}
        transition-all duration-200 hover:shadow-xl
        bg-gradient-to-br from-purple-500/10 to-purple-600/5
      `}>
        {/* Header */}
        <div className="p-3 pb-2 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-purple-400" />
              <div className="text-base font-semibold text-purple-100">
                {label}
              </div>
            </div>
            <Badge variant="outline" className="text-xs border-purple-500/50 text-purple-300">
              Action
            </Badge>
          </div>
          {description && (
            <div className="text-xs text-slate-400 mt-1">
              {description}
            </div>
          )}
        </div>

        {/* Steps List */}
        {steps.length > 0 && (
          <div className="p-3 pt-2 space-y-1.5">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex items-start gap-2 text-xs text-slate-300"
              >
                <CheckCircle2 className="w-3.5 h-3.5 text-purple-400 mt-0.5 flex-shrink-0" />
                <span>{step}</span>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {steps.length === 0 && !description && (
          <div className="p-3 pt-2">
            <div className="text-xs text-slate-500 italic">
              Executes external automation
            </div>
          </div>
        )}
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="out"
        style={{ right: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-purple-500"
      />
    </>
  );
};

export default HttpRequestNode;
